import clsx from 'clsx';
import { Anchor, Button, LabeledInput, Show } from 'components/atoms';
import { useRouter } from 'next/router';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ISO8601Date } from 'types';
import { usePaginationLimit, useTranslation, useUserTier } from 'utils/hooks';

import css from './EintragungenFilter.module.scss';
import PremiumCta from './PremiumCta/PremiumCta';

interface EintragungenFilterProps
{
  defaultValues?: Partial<EintragungenFilterFields>;
  aktuell: ISO8601Date;
  eintragungsart: 'neueintragungen' | 'insolvenzen' | 'scheinfirmen';
  dateOptions: number[];
}

interface EintragungenFilterFields
{
  bundesland: string;
  dateRange: string;
}

const resetValues: EintragungenFilterFields = {
  bundesland: '',
  dateRange: '',
};

const UnternehmensDetailsuche: React.FC<EintragungenFilterProps> = ({ defaultValues, aktuell, eintragungsart, dateOptions }) =>
{
  const { t } = useTranslation(['common', 'bundesländer', 'eintragungen']);
  const router = useRouter();
  const tier = useUserTier();
  const defaultLimit = usePaginationLimit();

  const isPlus = tier === 'plus';

  const {
    handleSubmit,
    register,
    watch,
    reset,
  } = useForm<EintragungenFilterFields>({ defaultValues, mode: 'onChange', reValidateMode: 'onChange' });

  const formWatch = watch();
  const bundesländer = t('bundesländer:list', { returnObjects: true }) as string[];

  const onSubmit: SubmitHandler<EintragungenFilterFields> = (filters) =>
  {
    const sanitized: Record<string, string> = {};

    Object.entries(filters).forEach(([key, value]) =>
    {
      if (value && value.length > 0)
      {
        sanitized[key] = value;
      }
    });

    if (Object.keys(sanitized).length > 0)
    {
      const query = new URLSearchParams({ ...sanitized, page: '1', limit: defaultLimit.toString() });
      router.push(`/l/${eintragungsart}?${query}`, undefined, { shallow: false });
    }
  };

  const onReset = () =>
  {
    reset(resetValues);

    const query = new URLSearchParams({ page: '1', limit: defaultLimit.toString() });
    router.push(`/l/${eintragungsart}?${query}`, undefined, { shallow: false });
  };

  const datePicker = (<LabeledInput
    id="datum"
    label={t('eintragungen:filter.datum.label')}
  >
    <select {... isPlus ? register('dateRange') : { defaultValue: '1' }}>
      <option value="">{t('eintragungen:filter.datum.aktuell', { date:aktuell })}</option>
      {dateOptions.map((option) => (
        <option key={option} disabled={!isPlus} value={option}>{t('eintragungen:filter.datum.lastN', { n:option })}</option>
      ))}
    </select>
  </LabeledInput>);

  return (
    <div className={clsx([css.container, isPlus ? css.plusContainer : undefined])}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={css.form}
      >
        {/* Bundesland */}
        <LabeledInput
          id="bundesland"
          label={t('eintragungen:filter.bundesland')}
        >
          <select {...register('bundesland')}>
            <option value="">{t('search:wizard.actions.select')}</option>
            <option disabled value={''}>{'------'}</option>
            {Object.entries(bundesländer).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
            <option disabled value={''}>{'------'}</option>
            <option value={'Ausland'}>{t('bundesländer:Ausland')}</option>
          </select>
        </LabeledInput>

        {/* Datum */}
        {isPlus && dateOptions.length > 0 && datePicker}

        <div className={css.actions}>
          <Anchor onClick={onReset}>{t('search:wizard.actions.reset')}</Anchor>
          <Button type="submit" disabled={!Object.values(formWatch).some(v => !!v)}>
            {t('search:wizard.actions.search')}
          </Button>
        </div>
      </form>
      <Show when={!isPlus}>
        <div className={css.cta}>
          <PremiumCta
            formField={datePicker}
          />
        </div>
      </Show>
    </div>
  );
};

export default UnternehmensDetailsuche;
